import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get, post } from "@/lib/api";
import { useAsync } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/components/RequireRole";
import { FieldError, FullPageLoader, usePageTitle } from "@/components/States";
import { Brand } from "@/components/layout/SidebarShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MODES = {
  invite: { check: "/auth/invite/", submit: "/auth/accept-invite", title: "Set your password", done: "Password saved. Welcome to Warrant Whizz!", missing: "This link is missing its token. Ask your manager for a new one." },
  reset: { check: "/auth/reset/", submit: "/auth/reset-password", title: "Choose a new password", done: "Password updated. You're signed in.", missing: "This link is missing its token. Ask for a new one from the sign-in page." },
};

const Invalid = ({ message }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-900 p-6">
    <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-xl" data-testid="set-password-invalid">
      <div className="mb-5 inline-block rounded-lg bg-slate-900 p-3"><Brand roleLabel="For warrant teams" /></div>
      <h1 className="font-display text-xl font-bold">Link invalid or expired</h1>
      <p className="mt-2 text-sm text-slate-600">{message}</p>
      <Link to="/login" className="mt-5 inline-block text-sm font-semibold text-blue-700 hover:underline" data-testid="set-password-login-link">Go to sign in</Link>
    </div>
  </div>
);

export default function SetPassword({ mode = "invite" }) {
  const m = MODES[mode];
  usePageTitle(m.title);
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setProfile } = useAuth();
  const navigate = useNavigate();
  const invite = useAsync(() => (token ? get(`${m.check}${encodeURIComponent(token)}`) : Promise.reject(new Error(m.missing))), [token, mode]);
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  if (invite.loading) return <FullPageLoader />;
  if (invite.error) return <Invalid message={invite.error} />;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (pw.password.length < 8) errs.password = "Use at least 8 characters";
    if (pw.password !== pw.confirm) errs.confirm = "Passwords do not match";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { user } = await post(m.submit, { token, password: pw.password });
      setProfile(user);
      toast.success(m.done);
      navigate(homeFor(user), { replace: true });
    } catch (err) {
      setErrors({ form: errorMessage(err) });
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-6">
      <form onSubmit={submit} noValidate className="w-full max-w-md space-y-5 rounded-xl border border-slate-200 bg-white p-8 shadow-xl" data-testid="set-password-form">
        <div className="inline-block rounded-lg bg-slate-900 p-3"><Brand roleLabel="For warrant teams" /></div>
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{m.title}</h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="set-password-email">{invite.data.full_name} · {invite.data.email}</p>
        </div>
        <div>
          <Label htmlFor="pw">New password</Label>
          <Input id="pw" type="password" autoComplete="new-password" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} className="mt-1.5 h-11" data-testid="set-password-input" />
          <FieldError message={errors.password} testId="set-password-error" />
        </div>
        <div>
          <Label htmlFor="pw2">Confirm password</Label>
          <Input id="pw2" type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1.5 h-11" data-testid="set-password-confirm-input" />
          <FieldError message={errors.confirm} testId="set-password-confirm-error" />
        </div>
        {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="set-password-form-error">{errors.form}</p>}
        <Button type="submit" disabled={busy} className="h-11 w-full bg-blue-700 hover:bg-blue-800" data-testid="set-password-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save password
        </Button>
      </form>
    </div>
  );
}
